import React, { useEffect } from 'react'
import {
  CommandPaletteOption,
  CompanyEntry,
  CompanyGroupEntry,
  EmptyState,
  PaymentEntry,
  PositionEntry,
  RouteEntry,
  ShiftEntry,
  UserEntry,
  WorkItemEntry,
} from './CommandPalette_Utils'
import { UserDetails } from './details/UserDetails'
import { CompanyDetails } from './details/CompanyDetails'
import { CompanyGroupDetails } from './details/CompanyGroupDetails'
import { RouteDetails } from './details/RouteDetails'
import { ShiftDetails } from './details/ShiftDetails'
import { WorkItemDetails } from './details/WorkItemDetails'
import { PaymentDetails } from './details/PaymentDetails'
import { PositionDetails } from './details/PositionDetails'
import { getHistoryType } from './history'

type DetailsProps = {
  selectedItem: CommandPaletteOption | null
  onNavigateToUser: (userId: string | number) => void
  onNavigateToCompany: (companyId: string | number) => void
  onNavigateToCompanyGroup: (companyGroupId: string | number) => void
  onNavigateToRoute: () => void
  onNavigateToShift: (shiftId: string | number) => void
  onNavigateToPayment: (paymentId: string | number) => void
  onNavigateToPosition: (positionId: string | number) => void
  onItemInteraction: (item: CommandPaletteOption) => void
}

export function Details({
  selectedItem,
  onNavigateToUser,
  onNavigateToCompany,
  onNavigateToCompanyGroup,
  onNavigateToRoute,
  onNavigateToShift,
  onNavigateToPayment,
  onNavigateToPosition,
  onItemInteraction,
}: DetailsProps) {
  const itemType = selectedItem ? getHistoryType(selectedItem) : null

  const handleDetailsClick = () => {
    if (selectedItem) {
      onItemInteraction(selectedItem)
    }
  }

  // handle navigation on 'enter' key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && selectedItem && itemType) {
        event.preventDefault()
        handleDetailsClick()
        if (itemType === 'users') {
          onNavigateToUser(selectedItem.id)
        } else if (itemType === 'companies') {
          onNavigateToCompany(selectedItem.id)
        } else if (itemType === 'company_groups') {
          onNavigateToCompanyGroup(selectedItem.id)
        } else if (itemType === 'shifts') {
          onNavigateToShift(selectedItem.id)
        } else if (itemType === 'work_items') {
          onNavigateToShift((selectedItem as WorkItemEntry).shiftId)
        } else if (itemType === 'payments') {
          onNavigateToPayment(selectedItem.id)
        } else if (itemType === 'positions') {
          onNavigateToPosition(selectedItem.id)
        } else if (itemType === 'routes') {
          onNavigateToRoute()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [selectedItem])

  const renderDetails = () => {
    if (!itemType || !selectedItem) {
      return <EmptyState>No result selected</EmptyState>
    }
    switch (itemType) {
      case 'users':
        return (
          <UserDetails
            result={selectedItem as UserEntry}
            onNavigateToUser={onNavigateToUser}
            onItemInteraction={onItemInteraction}
          />
        )
      case 'companies':
        return (
          <CompanyDetails
            company={selectedItem as CompanyEntry}
            onNavigateToCompany={onNavigateToCompany}
            onItemInteraction={onItemInteraction}
          />
        )
      case 'company_groups':
        return (
          <CompanyGroupDetails
            companyGroup={selectedItem as CompanyGroupEntry}
            onNavigateToCompanyGroup={onNavigateToCompanyGroup}
            onItemInteraction={onItemInteraction}
          />
        )
      case 'shifts':
        return (
          <ShiftDetails
            shift={selectedItem as ShiftEntry}
            onNavigateToShift={onNavigateToShift}
            onItemInteraction={onItemInteraction}
          />
        )
      case 'work_items':
        return (
          <WorkItemDetails
            workItem={selectedItem as WorkItemEntry}
            onNavigateToShift={onNavigateToShift}
            onItemInteraction={onItemInteraction}
          />
        )
      case 'payments':
        return (
          <PaymentDetails
            payment={selectedItem as PaymentEntry}
            onNavigateToPayment={onNavigateToPayment}
            onItemInteraction={onItemInteraction}
          />
        )
      case 'positions':
        return (
          <PositionDetails
            position={selectedItem as PositionEntry}
            onNavigate={onNavigateToPosition}
            onItemInteraction={onItemInteraction}
          />
        )
      case 'routes':
        return (
          <RouteDetails
            route={selectedItem as RouteEntry}
            onNavigate={onNavigateToRoute}
            onItemInteraction={onItemInteraction}
          />
        )
    }
  }

  return (
    <div
      onClick={handleDetailsClick}
      style={{
        width: '50%',
        padding: '16px',
        backgroundColor: '#f8f9fa',
        overflowY: 'auto',
        borderRadius: '0px 0px 8px 0px',
      }}
    >
      {renderDetails()}
    </div>
  )
}
